// features/projects/templates/professional/widgets/strongest-threads.js
// ═══════════════════════════════════════════════════════════
// STRONGEST THREADS — Tier-A widget
//
// Q: "Which findings are most defensible right now, and what
//     story does each one tell?"
// Top 3 findings ranked by Phase A status + confidence + n.
// Each thread carries: stat strip, deviation point, takeaway,
// intervention available, linked prediction, caveat.
// ═══════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';
import {
  WidgetShell, LayerSection, LayerAction, CaveatStrip,
} from './findings-status';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

const VERDICT_COLOR = {
  PARALLEL:        L.sage,
  'NEAR-PARALLEL': L.gold,
  DIVERGENT:       L.terra,
};
const PHASE_A_COLOR = {
  survived:               L.sage,
  'survived-respecified': L.gold,
  killed:                 L.terra,
};
const PHASE_A_LABEL = {
  survived:               'SURVIVED',
  'survived-respecified': 'SURVIVED · RESPECIFIED',
  killed:                 'KILLED',
};

export default function StrongestThreadsWidget({ featured }) {
  const { loading, comparisons, hypotheses, predictions } = useWidgetData();
  // Featured mode renders just the single strongest thread, deeper.
  // Used in the dashboard's spotlight slot.
  const limit = featured ? 1 : 3;
  const threads = useMemo(
    () => pickStrongestThreads(comparisons || [], hypotheses || [], predictions || [], limit),
    [comparisons, hypotheses, predictions, limit],
  );

  if (loading) return <WidgetShell label="Strongest Threads"><Loading /></WidgetShell>;

  if (threads.length === 0) {
    return (
      <WidgetShell label={featured ? 'Featured Thread' : 'Strongest Threads'}>
        <div style={{ fontSize: 12, color: L.mid, lineHeight: 1.5, fontFamily: FONT.sans }}>
          No substantive findings yet. Threads surface here as findings receive verdicts.
        </div>
      </WidgetShell>
    );
  }

  if (featured) {
    return (
      <WidgetShell label="Featured Thread · this week">
        <div style={{ fontSize: 11, color: L.mid, fontFamily: FONT.sans, lineHeight: 1.55, marginBottom: 12 }}>
          The most defensible finding right now — deviation point, interventions, and the prediction that tests it.
        </div>
        <ThreadRow thread={threads[0]} />
      </WidgetShell>
    );
  }

  return (
    <WidgetShell label={`Strongest Threads (${threads.length})`}>
      <div style={{ fontSize: 11, color: L.mid, fontFamily: FONT.sans, lineHeight: 1.55 }}>
        Ranked by Phase A status + confidence + sample size. Each thread shows
        its deviation point, what intervention applies, and which prediction
        tests it.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
        {threads.map(t => <ThreadRow key={t.comp.id} thread={t} />)}
      </div>
    </WidgetShell>
  );
}

function ThreadRow({ thread }) {
  const { comp, dyn, prediction, deviationPoint, takeaway, intervention, caveat } = thread;
  const verdict = (comp.verdict || '').toUpperCase();
  const verdictColor = VERDICT_COLOR[verdict] || L.dim;
  const phaseAColor = comp.phase_a_status ? PHASE_A_COLOR[comp.phase_a_status] : L.dim;
  const dynLabel = dyn?.label || comp.dynamic_key;

  return (
    <div style={{
      padding: '10px 12px',
      background: 'rgba(255,255,255,.02)',
      border: `1px solid ${L.b1}`,
      borderRadius: 5,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 8, fontFamily: FONT.mono, fontSize: 9,
      }}>
        <Pill color={verdictColor} text={verdict || 'PENDING'} />
        {comp.phase_a_status && (
          <Pill color={phaseAColor} text={`PHASE A · ${PHASE_A_LABEL[comp.phase_a_status]}`} />
        )}
        <span style={{ color: L.text, fontFamily: FONT.sans, fontSize: 12, fontWeight: 500 }}>
          {dynLabel}
        </span>
        <span style={{ marginLeft: 'auto', color: L.dim, letterSpacing: '0.04em', fontSize: 9 }}>
          eco {fmt(comp.eco_mean)} / human {fmt(comp.human_mean)} · d={fmt(comp.effect_size, 2)}
        </span>
      </div>
      {deviationPoint && <LayerSection label="Deviation point">{deviationPoint}</LayerSection>}
      {takeaway && <LayerSection label="Takeaway" accent={L.gold}>{takeaway}</LayerSection>}
      {intervention && (
        <LayerSection label="Intervention available" accent={L.sage}>
          {intervention}
        </LayerSection>
      )}
      {prediction && (
        <LayerAction
          accent={L.gold}
          label="Tested by forward prediction"
          why={`Resolves ${formatDate(prediction.resolution_date)} — ${truncate(prediction.claim, 100)}`}
        />
      )}
      {caveat && <CaveatStrip text={caveat} />}
    </div>
  );
}

function Pill({ color, text }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 3,
      border: `1px solid ${color}60`,
      background: `${color}15`, color,
      letterSpacing: '0.08em', fontWeight: 600, textTransform: 'uppercase',
      fontSize: 'inherit', fontFamily: 'inherit',
    }}>{text}</span>
  );
}

// ─── Composition ─────────────────────────────────────────────
function pickStrongestThreads(comps, hypotheses, predictions, limit = 3) {
  const substantive = comps.filter(isSubstantive);
  const ranked = substantive.slice().sort((a, b) => threadScore(b) - threadScore(a)).slice(0, limit);
  const predByComp = new Map();
  for (const p of predictions) {
    const cur = predByComp.get(p.comparison_id);
    if (!cur || new Date(p.resolution_date) < new Date(cur.resolution_date)) {
      predByComp.set(p.comparison_id, p);
    }
  }
  return ranked.map(comp => {
    const dyn = DYN_BY_KEY[comp.dynamic_key];
    const prediction = predByComp.get(comp.id) || null;
    return {
      comp, dyn, prediction,
      deviationPoint: composeDeviationPoint(comp, dyn),
      takeaway: composeTakeaway(comp),
      intervention: composeIntervention(dyn),
      caveat: composeCaveat(comp),
    };
  });
}
function threadScore(c) {
  let s = 0;
  if (c.phase_a_status === 'survived') s += 100;
  else if (c.phase_a_status === 'survived-respecified') s += 80;
  if ((c.confidence || '').toUpperCase() === 'HIGH') s += 50;
  s += Math.min(20, Math.log10(Math.max(1, (Number(c.eco_n) || 0) + (Number(c.human_n) || 0))) * 4);
  return s;
}
function composeDeviationPoint(comp, dyn) {
  const dynLabel = dyn?.label || comp.dynamic_key;
  const respec = comp.phase_a_modules?.counter_narrative?.residual_caveat;
  if (respec) return `Phase A respec: ${respec}`;
  const verdict = (comp.verdict || '').toUpperCase();
  if (verdict === 'PARALLEL' || verdict === 'NEAR-PARALLEL') {
    if (Math.abs(Number(comp.effect_size) || 0) < 0.1) {
      return `${dynLabel} ratios are statistically indistinguishable at full sample (d=${fmt(comp.effect_size, 3)}). The deviation point is whether this similarity holds under restricted subsets.`;
    }
    return `${dynLabel} shows a small but real cross-domain gap (d=${fmt(comp.effect_size, 2)}).`;
  }
  if (verdict === 'DIVERGENT') {
    const sign = (Number(comp.effect_size) || 0) > 0 ? 'higher' : 'lower';
    const which = sign === 'higher' ? 'ecological' : 'human';
    return `${dynLabel} diverges (d=${fmt(comp.effect_size, 2)}); ${which} systems show the larger ratio.`;
  }
  return `${dynLabel} has not yet produced a verdict.`;
}
function composeTakeaway(comp) {
  if (comp.phase_a_status === 'survived-respecified') return 'Survived adversarial review in respecified form. The respec narrowed the claim — that\'s the methodology working as designed (precision over breadth).';
  if (comp.phase_a_status === 'survived') return 'Survived adversarial review intact. Defensible to cite externally with the standard caveats about sample size and dataset coverage.';
  if (comp.phase_a_status === 'killed') return 'Phase A killed the finding. Treat as evidence against, not as a null result.';
  const verdict = (comp.verdict || '').toUpperCase();
  if (verdict === 'DIVERGENT') return 'Statistically robust divergence, but no adversarial review yet. Treat as suggestive until tested.';
  return 'Awaiting Phase A red-team. Numbers are clean but not yet stress-tested.';
}
function composeIntervention(dyn) {
  if (!dyn) return null;
  const eco = dyn.ecological_prescription;
  if (!eco) return null;
  return eco.split(/(?<=[.!?])\s/)[0] + ' The human-system parallel applies the same intervention shape.';
}
function composeCaveat(comp) {
  const parts = [];
  const ecoN = Number(comp.eco_n) || 0;
  const humanN = Number(comp.human_n) || 0;
  if (humanN < 100) parts.push(`human-side n=${humanN} is small`);
  else if (ecoN < 100 && ecoN > 0) parts.push(`eco-side n=${ecoN} is small`);
  if (!comp.phase_a_status) parts.push('Phase A not yet run');
  if (comp.caveat) parts.push(comp.caveat);
  return parts.length ? parts.join(' · ') : null;
}
function isSubstantive(c) {
  return c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase());
}
function fmt(n, digits = 4) {
  if (n === null || n === undefined) return '—';
  if (typeof n !== 'number') return String(n);
  if (Math.abs(n) >= 10000) return n.toExponential(2);
  return n.toFixed(digits);
}
function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}
function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
function Loading() {
  return <div style={{ fontSize: 12, color: L.dim, fontStyle: 'italic', fontFamily: FONT.sans }}>Loading…</div>;
}
